import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Study, StudyStatsResponse } from "@/types/api";

/**
 * Hook to get the count of actively running studies.
 *
 * Merges two data sources to balance accuracy with responsiveness:
 * 1. Page caches (["studies", page, filter, ...]) — reflect optimistic mutation
 *    updates immediately (e.g., user pauses a study, count drops instantly)
 * 2. Stats cache (["studies", "stats"]) — accurate server-side count, used as
 *    fallback when no page caches with study arrays exist (e.g., user is on
 *    the settings page)
 *
 * Page caches are preferred when available because optimistic mutations update
 * them synchronously, while the stats cache lags until the server refetch
 * completes (~50-200ms).
 *
 * Only counts studies with status === "running" (excludes paused, queued, etc.)
 */
export function useActiveStudyCount() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["studies", "active-count"],
    queryFn: () => {
      // Scan ALL cached studies queries for optimistic mutation updates
      const runningStudyIds = new Set<string>();
      let hasPageCacheData = false;

      const queriesData = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({
        queryKey: ["studies"],
      });

      for (const [queryKey, data] of queriesData) {
        const keyStr = JSON.stringify(queryKey);
        if (keyStr.includes("active-count") || keyStr.includes("stats")) {
          continue;
        }

        if (data?.studies && Array.isArray(data.studies)) {
          hasPageCacheData = true;
          for (const study of data.studies) {
            if (study.status === "running") {
              runningStudyIds.add(study.study_id);
            }
          }
        }
      }

      // When page caches exist, use them (reflects optimistic updates instantly)
      if (hasPageCacheData) {
        return {
          count: runningStudyIds.size,
          studyIds: Array.from(runningStudyIds),
        };
      }

      // Fallback: stats cache (when no page caches available, e.g., non-studies pages)
      const statsData = queryClient.getQueryData<StudyStatsResponse>(["studies", "stats"]);
      return {
        count: statsData?.status_counts?.running || 0,
        studyIds: [] as string[],
      };
    },
    refetchInterval: 2000,
    staleTime: 1000,
    refetchOnWindowFocus: false,
  });
}

export type ActiveStudyCountResult = ReturnType<typeof useActiveStudyCount>;
